import { z } from 'zod';
import { IdSchema, PluginExtensionIdSchema, type PluginExtensionId } from './common.js';

/**
 * AST de expresiones del Semantic Model. Las columnas calculadas, dimensiones y
 * medidas calculadas, filtros de seguridad y reglas se describen con este arbol y NO
 * con SQL: cada dialecto del Query Engine lo compila a su lenguaje. Un futuro editor
 * de formulas convertira texto (`DIVIDE(SUM(...), ...)`) en este AST.
 */
export const UNARY_OPERATORS = ['not', 'negate'] as const;
export const BINARY_OPERATORS = [
  '+',
  '-',
  '*',
  '/',
  '%',
  '=',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'and',
  'or',
] as const;
export const AGGREGATE_FUNCTIONS = [
  'sum',
  'avg',
  'min',
  'max',
  'count',
  'count-distinct',
  'median',
] as const;

/** Funciones escalares del catalogo de la plataforma (fila a fila). */
export const SCALAR_FUNCTIONS = [
  'divide',
  'if',
  'coalesce',
  'nullif',
  'abs',
  'round',
  'floor',
  'ceil',
  'power',
  'concat',
  'lower',
  'upper',
  'trim',
  'length',
  'substring',
  'year',
  'quarter',
  'month',
  'week',
  'day',
  'date-trunc',
  'date-diff',
  'today',
  'now',
] as const;

export type UnaryOperator = (typeof UNARY_OPERATORS)[number];
export type BinaryOperator = (typeof BINARY_OPERATORS)[number];
export type AggregateFunction = (typeof AGGREGATE_FUNCTIONS)[number];
export type ScalarFunction = (typeof SCALAR_FUNCTIONS)[number];

export interface FunctionSignature {
  readonly minArgs: number;
  /** `null` = variadica. */
  readonly maxArgs: number | null;
  readonly category: 'math' | 'logical' | 'text' | 'date';
}

/**
 * Firma de cada funcion escalar. Es la fuente que usaran el parser de formulas,
 * la validacion semantica y los dialectos; aqui solo se declara, no se evalua.
 */
export const EXPRESSION_FUNCTION_CATALOG: Readonly<Record<ScalarFunction, FunctionSignature>> = {
  divide: { minArgs: 2, maxArgs: 3, category: 'math' },
  if: { minArgs: 2, maxArgs: 3, category: 'logical' },
  coalesce: { minArgs: 1, maxArgs: null, category: 'logical' },
  nullif: { minArgs: 2, maxArgs: 2, category: 'logical' },
  abs: { minArgs: 1, maxArgs: 1, category: 'math' },
  round: { minArgs: 1, maxArgs: 2, category: 'math' },
  floor: { minArgs: 1, maxArgs: 1, category: 'math' },
  ceil: { minArgs: 1, maxArgs: 1, category: 'math' },
  power: { minArgs: 2, maxArgs: 2, category: 'math' },
  concat: { minArgs: 1, maxArgs: null, category: 'text' },
  lower: { minArgs: 1, maxArgs: 1, category: 'text' },
  upper: { minArgs: 1, maxArgs: 1, category: 'text' },
  trim: { minArgs: 1, maxArgs: 1, category: 'text' },
  length: { minArgs: 1, maxArgs: 1, category: 'text' },
  substring: { minArgs: 2, maxArgs: 3, category: 'text' },
  year: { minArgs: 1, maxArgs: 1, category: 'date' },
  quarter: { minArgs: 1, maxArgs: 1, category: 'date' },
  month: { minArgs: 1, maxArgs: 1, category: 'date' },
  week: { minArgs: 1, maxArgs: 1, category: 'date' },
  day: { minArgs: 1, maxArgs: 1, category: 'date' },
  'date-trunc': { minArgs: 2, maxArgs: 2, category: 'date' },
  'date-diff': { minArgs: 3, maxArgs: 3, category: 'date' },
  today: { minArgs: 0, maxArgs: 0, category: 'date' },
  now: { minArgs: 0, maxArgs: 0, category: 'date' },
};

/** Nombre de funcion: del catalogo integrado o aportada por un plugin (`plugin:<id>`). */
export type FunctionName = ScalarFunction | PluginExtensionId;

export type Expression =
  | { type: 'literal'; value: string | number | boolean | null }
  | { type: 'column'; columnId: string }
  | { type: 'measure'; measureId: string }
  | { type: 'unary'; operator: UnaryOperator; operand: Expression }
  | { type: 'binary'; operator: BinaryOperator; left: Expression; right: Expression }
  | { type: 'function'; name: FunctionName; args: Expression[] }
  | {
      type: 'aggregate';
      function: AggregateFunction;
      argument?: Expression | undefined;
      filter?: Expression | undefined;
    }
  | {
      type: 'case';
      branches: { when: Expression; then: Expression }[];
      else?: Expression | undefined;
    };

export const FunctionNameSchema = z.union([z.enum(SCALAR_FUNCTIONS), PluginExtensionIdSchema]);

export const ExpressionSchema: z.ZodType<Expression> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.strictObject({
      type: z.literal('literal'),
      value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    }),
    z.strictObject({ type: z.literal('column'), columnId: IdSchema }),
    z.strictObject({ type: z.literal('measure'), measureId: IdSchema }),
    z.strictObject({
      type: z.literal('unary'),
      operator: z.enum(UNARY_OPERATORS),
      operand: ExpressionSchema,
    }),
    z.strictObject({
      type: z.literal('binary'),
      operator: z.enum(BINARY_OPERATORS),
      left: ExpressionSchema,
      right: ExpressionSchema,
    }),
    z.strictObject({
      type: z.literal('function'),
      name: FunctionNameSchema,
      args: z.array(ExpressionSchema),
    }),
    z.strictObject({
      type: z.literal('aggregate'),
      function: z.enum(AGGREGATE_FUNCTIONS),
      argument: ExpressionSchema.optional(),
      filter: ExpressionSchema.optional(),
    }),
    z.strictObject({
      type: z.literal('case'),
      branches: z.array(z.strictObject({ when: ExpressionSchema, then: ExpressionSchema })).min(1),
      else: ExpressionSchema.optional(),
    }),
  ]),
);

/** Texto de formula tal como lo escribio el usuario; se conserva solo para edicion. */
export const FormulaTextSchema = z.string().min(1).max(8000);
